'use client';

import { useRef, useMemo, useEffect } from 'react';
import { notFound } from 'next/navigation';
import useClientStore from '@/store/useClientStore';
import useThemeStore from '@/store/useThemeStore';
import { useTranslation } from '@/i18n/hooks/useTranslation';
import { forcedSiteLanguage } from '@/lib/country';
import { getPageBuilderData, getHiddenSystemPages, hasFooterNode, hasNodeOfType } from '@/lib/page-builder-data';
import { usaNavbarInline } from '@/lib/navbarPorTenant';
import { useBuilderReady } from '@/hooks/useBuilderReady';
import { familiasDelArbol, cargarFuentes } from '@/lib/fuentes-del-arbol';
import BuilderRenderer from '@/app/BuilderRenderer';
import { Footer as StaticFooter } from '@/components/layout/Footer';
import PageLoading from '@/components/layout/PageLoading';

interface Props {
  slug: string;
  fallback: React.ReactNode;
  /**
   * Componente sin el cual la página no tiene sentido. Si el árbol del builder
   * no lo trae (el dealer lo borró), se monta `fallback` en vez de servir una
   * página vacía. Además blinda la página contra "ocultarla": no cae al 404.
   * Se usa en `/agendar`, que es un link que las automotoras reparten.
   */
  requireComponent?: string;
}

// Global cache — persists across page navigations, no re-parsing on revisit
// Separate caches for default language and translated versions
const pageCacheDefault = new Map<string, { light: any; dark: any }>();
const pageCacheTranslated = new Map<string, { light: any; dark: any }>();

// Loader centrado mientras carga la estructura del builder. Mejor que una pantalla
// en blanco: comunica "está cargando" en vez de parecer que la página está rota.
//
// Es el MISMO que sirve de `loading.tsx` en cada ruta, a propósito: al navegar
// se encadenan los dos —primero el de la ruta, después éste— y si no fueran
// idénticos el relevo se vería como un parpadeo.
const BuilderLoading = PageLoading;

export default function BuilderPageWrapper({ slug, fallback, requireComponent }: Props) {
  const { client, isLoading } = useClientStore();
  const { theme } = useThemeStore();
  const { currentLanguage } = useTranslation();
  const builderStatus = useBuilderReady();
  const clientIdRef = useRef<any>(null);

  // Invalidate caches if client changes
  if (client?.id && client.id !== clientIdRef.current) {
    clientIdRef.current = client.id;
    pageCacheDefault.clear();
    pageCacheTranslated.clear();
  }

  const cfg = useMemo(() => {
    const config = client?.client_website_config;
    return Array.isArray(config) ? config[0] : config;
  }, [client?.client_website_config]);

  // Determine if we should use AI translations
  // Builder content is always written in Spanish; translations are always es→en.
  // Apply translations when the user is viewing in English, regardless of default_language.
  // EE.UU. (inglés obligatorio por país): el builder ya está escrito en inglés
  // por la automotora; se muestra TAL CUAL, sin traducción es→en.
  const hasLanguageSelector = !!(client as any)?.has_language_selector;
  const needsTranslation =
    hasLanguageSelector && currentLanguage === 'en' && forcedSiteLanguage(client?.country) !== 'en';

  // Parse translations from config
  const translations = useMemo(() => {
    if (!needsTranslation || !cfg?.translations) return null;
    try {
      return typeof cfg.translations === 'object' ? cfg.translations : JSON.parse(cfg.translations);
    } catch { return null; }
  }, [needsTranslation, cfg?.translations]);


  // Páginas de sistema que el cliente eliminó desde el builder
  const hiddenSystemPages = useMemo(() => getHiddenSystemPages(cfg), [cfg]);

  // Tenants que traen su navbar dentro del árbol de CADA página (ver
  // `lib/navbarPorTenant`). Para el resto vale lo de siempre: el navbar vive en
  // la home y el layout lo monta fijo en las demás.
  const navbarInline = usaNavbarInline(client?.id);

  // Pick the right cache
  const cache = needsTranslation ? pageCacheTranslated : pageCacheDefault;

  // Load & cache current page synchronously
  if (!isLoading && cfg?.is_enabled && cfg?.elements_structure && !cache.has(slug)) {
    cache.set(slug, {
      light: getPageBuilderData(cfg, slug, 'light', translations, navbarInline),
      dark: getPageBuilderData(cfg, slug, 'dark', translations, navbarInline),
    });

    // Pre-cache ALL other pages in background
    const allSlugs = ['home', 'financing', 'consignments', 'buy-direct', 'we-search-for-you', 'contact', 'about', 'agendar'];
    const precache = () => {
      for (const s of allSlugs) {
        if (!cache.has(s)) {
          cache.set(s, {
            light: getPageBuilderData(cfg, s, 'light', translations, navbarInline),
            dark: getPageBuilderData(cfg, s, 'dark', translations, navbarInline),
          });
        }
      }
    };
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(precache);
    } else {
      setTimeout(precache, 100);
    }
  }

  // Fuentes elegidas por elemento desde el editor. El layout carga una lista
  // fija; acá se suman las que esta página use y no estén en esa lista, para que
  // el Builder pueda ofrecer todo Google Fonts sin que este repo conozca el
  // catálogo. Aditivo: si no encuentra ninguna, no hace nada.
  const arbolActual = cache.get(slug);
  useEffect(() => {
    if (!arbolActual) return;
    const familias = [
      ...familiasDelArbol(arbolActual.light),
      ...familiasDelArbol(arbolActual.dark),
    ];
    if (familias.length) cargarFuentes(familias);
  }, [arbolActual]);
  if (isLoading || !client?.id) return <BuilderLoading />;

  // elements_structure se carga en background (puede ser 10+ MB para tenants pesados).
  // Mientras llega, no mostrar fallback templates (causaría un cambio de UI completo
  // cuando llegue la config real): mostramos un loader. Si tras el timeout la
  // estructura nunca llegó (status 'fallback'), degradamos al template tradicional
  // para no dejar la página colgada con un spinner infinito.
  const isStructurePending = cfg?.is_enabled && !cfg?.elements_structure;
  if (isStructurePending) {
    return builderStatus === 'fallback' ? <>{fallback}</> : <BuilderLoading />;
  }

  // Página de sistema eliminada por el cliente → 404 real (la ruta física existe
  // igual, así que sin este check mostraría el fallback tradicional). 'home' nunca.
  // Las páginas con `requireComponent` tampoco: son links que la automotora
  // reparte (WhatsApp/Instagram) y no pueden empezar a dar 404 desde el editor.
  if (cfg?.is_enabled && slug !== 'home' && !requireComponent && hiddenSystemPages.includes(slug)) {
    notFound();
  }

  const cached = cache.get(slug);
  if (!cached) return <>{fallback}</>;

  const data = theme === 'dark' ? (cached.dark || cached.light) : (cached.light || cached.dark);
  if (!data) return <>{fallback}</>;

  // El dealer puede maquetar alrededor, pero si borró el bloque que da sentido a
  // la página servimos el fallback: mejor el formulario de siempre que una página
  // decorativa donde el visitante no puede agendar.
  if (requireComponent && !hasNodeOfType(data, requireComponent)) {
    return <>{fallback}</>;
  }

  // En páginas no-home el navbar sticky horneado se elimina (ver getPageBuilderData)
  // y lo reemplaza el Navbar fijo del layout (overlay, no ocupa flujo). Reponemos
  // los ~64px (h-16) que ocupaba el navbar sticky para que la primera sección no
  // quede tapada. La home conserva su navbar inline, así que no lleva padding.
  // Los tenants de `usaNavbarInline` tampoco: ahí el navbar sigue dentro del
  // árbol de la página y ocupa flujo, así que no hay overlay que compensar.
  // Red de seguridad: si al árbol de esta página le falta el footer (se puede
  // borrar desde el editor y `home` no se regenera nunca), montamos el estático.
  // ConditionalFooter devuelve null en páginas del builder, así que sin esto la
  // página queda literalmente sin pie — le pasó a 4 tenants en producción.
  const footerDeRespaldo = !hasFooterNode(data) ? <StaticFooter /> : null;

  if (slug === 'home' || navbarInline) {
    return (
      <>
        <BuilderRenderer data={data} themeKey={`${theme}-${currentLanguage}`} fallback={fallback} />
        {footerDeRespaldo}
      </>
    );
  }
  return (
    <>
      <div className="pt-16">
        <BuilderRenderer data={data} themeKey={`${theme}-${currentLanguage}`} fallback={fallback} />
      </div>
      {footerDeRespaldo}
    </>
  );
}
